import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TransportService } from '@/services/transport/transportService';
import { Transport, CreateTransportRequest, UpdateTransportRequest } from '@/types/transport';

export const useTransport = (page: number = 1, limit: number = 10) => {
  const queryClient = useQueryClient();

  // Create transport mutation
  const createTransportMutation = useMutation({
    mutationFn: ({ data, isAdmin }: { data: CreateTransportRequest; isAdmin?: boolean }) => 
      TransportService.createTransport(data, isAdmin || false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transports'] });
      queryClient.invalidateQueries({ queryKey: ['transports-by-auction'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-listing-details'] });
    },
  });

  // Update transport mutation
  const updateTransportMutation = useMutation({
    mutationFn: ({ data, isAdmin }: { data: UpdateTransportRequest; isAdmin?: boolean }) => 
      TransportService.updateTransport(data, isAdmin || false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transports'] });
      queryClient.invalidateQueries({ queryKey: ['transports-by-auction'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-listing-details'] });
    },
  });

  // Delete transport mutation
  const deleteTransportMutation = useMutation({
    mutationFn: (id: number) => TransportService.deleteTransport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transports'] });
      queryClient.invalidateQueries({ queryKey: ['transports-by-auction'] });
    },
  });

  return {
    // Mutations
    createTransportAsync: (data: CreateTransportRequest, isAdmin?: boolean) => 
      createTransportMutation.mutateAsync({ data, isAdmin }),
    updateTransportAsync: (data: UpdateTransportRequest, isAdmin?: boolean) => 
      updateTransportMutation.mutateAsync({ data, isAdmin }),
    deleteTransportAsync: deleteTransportMutation.mutateAsync,
    
    // Mutation states
    isCreating: createTransportMutation.isPending,
    isUpdating: updateTransportMutation.isPending,
    isDeleting: deleteTransportMutation.isPending,
  };
};